import {
  ChangeEventHandler,
  KeyboardEventHandler,
  RefObject,
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import { useOnClickOutside } from '@inno-tools/react-hooks'
import cn from 'classnames'

import { maxItemNameLength } from '../../constants'
import { validateItemName } from '../../utils'
import styles from './InlineNameInput.module.scss'
import { InlineNameInputProps } from './InlineNameInput.types'

export const InlineNameInput = memo<InlineNameInputProps>(
  ({ initialValue, extension, onSubmit, onCancel }) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [hasError, setHasError] = useState(false)

    const submit = useCallback(() => {
      onSubmit((inputRef.current?.value ?? '').trim())
    }, [onSubmit])

    const handleKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
      e => {
        if (e.key === 'Enter') {
          e.preventDefault()
          submit()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          onCancel()
        }
      },
      [submit, onCancel]
    )

    const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      e => {
        setHasError(Boolean(validateItemName(e.target.value.trim())))
      },
      []
    )

    useOnClickOutside(inputRef as RefObject<HTMLElement>, submit)

    useLayoutEffect(() => {
      const el = inputRef.current
      if (!el) return

      el.value = initialValue
      el.focus()

      const dotIndex = initialValue.lastIndexOf('.')
      const ext = dotIndex >= 0 ? initialValue.slice(dotIndex + 1) : undefined
      const selectionEnd =
        extension && ext === extension ? dotIndex : initialValue.length

      el.setSelectionRange(0, selectionEnd)
      setHasError(Boolean(validateItemName(initialValue.trim())))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <input
        ref={inputRef}
        type='text'
        className={cn(styles.input, { [styles.hasError]: hasError })}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onDoubleClick={e => e.stopPropagation()}
        onDragStart={e => e.preventDefault()}
        draggable={false}
        maxLength={maxItemNameLength}
        spellCheck={false}
        autoComplete='off'
      />
    )
  }
)
